import cx from 'classnames';
import { History, LocationDescriptorObject } from 'history';
import memoize from 'lodash-es/memoize';
import pick from 'lodash-es/pick';
import upperFirst from 'lodash-es/upperFirst';
import {
    BACK_TO_TOPICS,
    FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE,
    FORUM_TOPICS_NOT_FOUND_query,
    LIST_OF_FORUMS_BLUEHORSE,
    LOAD_PREVIOUS,
    NEW_FEMININE,
} from 'mk/autogenerated/translations/ListOfForums.9250c151c90d902904f128deb6beb2cb'
import { ARCHIVED_DAYS_WITH_COVER_PHOTO, CONTESTS_COVER_PHOTO, TESTINGS_COVER_PHOTO } from 'mk2/apps/forum/constants';
import { forumBrandDaysArchiveUrl, forumListOfForumsUrl, forumTopicsCategoryUrl } from 'mk2/apps/forum/urls';
import Btn, { BtnType } from 'mk2/components/Btn';
import Failure from 'mk2/components/errors/Failure';
import InfiniteList from 'mk2/components/InfiniteListLoadable';
import Link from 'mk2/components/Link';
import ListHeadingItem from 'mk2/components/ListHeadingItem';
import ListItem, { ListItemPlaceholder } from 'mk2/components/ListItem';
import { ListItemIcon } from 'mk2/components/ListItemIcon';
import NoResults from 'mk2/components/NoResults';
import { authOnly } from 'mk2/helpers/decorators';
import { dateDiff } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import { isFailure, isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { interpolate } from 'mk2/services/i18n';
import { parse, stringify } from 'query-string';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Route } from 'react-router';
import { ListRowProps } from 'react-virtualized';
import styles from './ListOfForums.mscss';
import { Props } from './ListOfForumsPage';
import LocationDescriptor = History.LocationDescriptor;

const ROW_HEIGHT = 44;

const lessThan14Days = (d: string | Date) => {
    const { diffDays, diffSeconds } = dateDiff(d, new Date());
    return diffDays < 14;
};

class ListOfForums extends React.Component<Props> {

    private onItemClickedStoreEvent = memoize((itemLoc: LocationDescriptor) => () => {
        const { onStoreEvent } = this.props;
        onStoreEvent('forum_category_clicked', {
            slug: (itemLoc as LocationDescriptorObject).pathname || itemLoc,
            from: 'topics',
        });
    });

    public componentWillMount() {
        const { loadingState, renderedAt } = this.props;

        // Load only if not loaded on server and is not older than 30s
        if ((loadingState !== LoadingState.SUCCESS_ON_SERVER && loadingState !== LoadingState.FAILURE_ON_SERVER) || renderedAt < Date.now() - 30 * 1000) {
            this.load();
        }
    }

    public shouldComponentUpdate(n: Props) {
        const p = this.props;

        return p.items !== n.items
            || p.after !== n.after
            || p.loadingState !== n.loadingState;
    }

    public componentDidUpdate(prevProps: Props) {
        // After has changed
        if (this.props.after !== prevProps.after) {
            this.load();
        }

        // State change LOADING -> FAILURE and we have some topics
        if (prevProps.loadingState === LoadingState.LOADING
            && this.props.loadingState === LoadingState.FAILURE
            && this.props.items.length) {
            this.props.onFailureToast();
        }
    }

    public render() {
        const {
            className, errorMessage, hasNext, hasPrevious, items,
            loadingState, location, staticContext,
        } = this.props;

        // InfiniteScroll data
        // @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
        const topicsRowCount = hasNext ? items.length + 1 : items.length;
        const rowCount = (!hasNext && isSuccess(loadingState)) ? topicsRowCount + 4 : topicsRowCount;

        return (
            <div className={cx(styles.ListOfForums, className)}>
                <Route exact path={forumTopicsCategoryUrl} children={this.renderHeaders} />

                {hasPrevious && isSuccess(loadingState) && (
                    <div className={styles.ListOfForums__loadPrevious}>
                        <Link
                            className={styles.ListOfForums__loadPrevious__link}
                            to={{ ...location, search: `?${stringify({ ...parse(location.search), after: -items[0].topicId })}`}}
                            onClick={this.loadPrevious}
                        >
                            {LOAD_PREVIOUS}
                        </Link>
                    </div>
                )}

                {loadingState === LoadingState.INIT || (loadingState === LoadingState.LOADING && items.length === 0) ? (
                    <span>
                        <ListItemPlaceholder icon isLast={false} />
                        <ListItemPlaceholder icon isLast={false} />
                        <ListItemPlaceholder icon isLast={false} />
                        <ListItemPlaceholder icon isLast={false} />
                        <ListItemPlaceholder icon isLast />
                    </span>
                ) : null}

                {isSuccess(loadingState) && items.length === 0 ? (
                    <NoResults message={interpolate(FORUM_TOPICS_NOT_FOUND_query, {query: ''})} />
                ) : null}

                {isFailure(loadingState) && items.length === 0 ? (
                    <Failure
                        staticContext={staticContext}
                        errorMessage={errorMessage}
                        onRetry={this.load}
                        backButton={
                            <Btn
                                type={BtnType.Blue}
                                label={BACK_TO_TOPICS}
                                icon="fa-arrow-left"
                                link={forumListOfForumsUrl}
                            />
                        }
                    />
                ) : null}

                {items.length ? (
                    <InfiniteList
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={rowCount}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={(props: ListRowProps) => this.rowRenderer(props) /* tslint:disable-line */}
                    />
                ) : null}
            </div>
        );
    }

    private load = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, after } = this.props;
        onLoad(requestUser.username, after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, items } = this.props;
        const after = items[items.length - 1].topicId;
        onLoad(requestUser.username, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private loadPrevious = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, items } = this.props;
        const after = -items[0].topicId;
        onLoad(requestUser.username, after);
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.items.length;

    private rowRenderer = ({ index, key, style }) => {
        const {
            brandDaysRowNo, contestsRowNo, contestsTopic, hasNext, items, itemToTopicUrl, systemCatRowNo,
            testingsRowNo, testingsTopic,
        } = this.props;

        if (index === systemCatRowNo) {
            return (
                <ListHeadingItem
                    isSectionStart={index > 0}
                    key={key}
                    style={style}
                >
                    {LIST_OF_FORUMS_BLUEHORSE}
                </ListHeadingItem>
            );
        }

        if (index === contestsRowNo) {
            return (
                <ListItem
                    hasMore
                    icon={<ListItemIcon lazy photo={CONTESTS_COVER_PHOTO} />}
                    isLast={false}
                    key={key}
                    onClick={this.onItemClickedStoreEvent(itemToTopicUrl(contestsTopic))}
                    style={style}
                    to={itemToTopicUrl(contestsTopic)}
                >
                    {contestsTopic.name}
                </ListItem>
            );
        }

        if (index === testingsRowNo) {
            return (
                <ListItem
                    hasMore
                    icon={<ListItemIcon lazy photo={TESTINGS_COVER_PHOTO} />}
                    isLast={false}
                    key={key}
                    onClick={this.onItemClickedStoreEvent(itemToTopicUrl(testingsTopic))}
                    style={style}
                    to={itemToTopicUrl(testingsTopic)}
                >
                    {testingsTopic.name}
                </ListItem>
            );
        }

        if (index === brandDaysRowNo) {
            return (
                <ListItem
                    hasMore
                    icon={<ListItemIcon lazy photo={ARCHIVED_DAYS_WITH_COVER_PHOTO} />}
                    isLast
                    key={key}
                    onClick={this.onItemClickedStoreEvent(forumBrandDaysArchiveUrl)}
                    style={style}
                    to={forumBrandDaysArchiveUrl}
                >
                    {FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE}
                </ListItem>
            );
        }

        if (!this.isRowLoaded({ index })) {
            return (
                <ListItemPlaceholder key={key} style={style} icon isLast />
            );
        }

        const item = items[index];
        const nextItem = (index + 1 < items.length) ? items[index + 1] : null;

        let listItem;
        if (item.type === 'category') {
            const category = item.category;
            listItem = (
                <ListHeadingItem
                    isSectionStart={index > 0}
                    key={key}
                    style={style}
                >
                    {upperFirst(category.name)}
                </ListHeadingItem>
            );
        } else {
            const topic = item.topic;
            const isNew = lessThan14Days(item.topic.created);
            listItem = (
                <ListItem
                    count={topic.numThreads}
                    icon={<ListItemIcon lazy photo={topic.coverPhoto} />}
                    isLast={(!hasNext && items.length === index + 1) || (nextItem && nextItem.type === 'category')}
                    key={key}
                    onClick={this.onItemClickedStoreEvent(itemToTopicUrl(topic))}
                    rightElement={isNew ? (
                        <div className={styles.ListOfForums__badgeNew}>{NEW_FEMININE}</div>
                    ) : null}
                    style={style}
                    to={itemToTopicUrl(topic)}
                >
                    {upperFirst(topic.name)}
                </ListItem>
            );
        }

        return listItem;
    };

    private renderHeaders = ({ match }) => {
        const { loadingState, hasNext, hasPrevious, baseUrl, items, location } = this.props;
        if (match && isSuccess(loadingState)) {
            const qs = pick<{ after: string }>(parse(location.search), [ 'after' ]);

            return (
                <Helmet>
                    {hasNext && <link rel="next" href={baseUrl + url(forumListOfForumsUrl, {}, {...qs, after: items[items.length - 1].topicId})} />}
                    {hasPrevious && <link rel="prev" href={baseUrl + url(forumListOfForumsUrl, {}, { ...qs, after: -items[0].topicId })} />}
                </Helmet>
            );
        }

        return null;
    };
}

export default ListOfForums;

@authOnly()
export class AuthOnlyForumTopics extends ListOfForums {}
